import { Box, Paper, Typography, Tooltip, Chip, Slider, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiBarChart2, FiCheck, FiLayers, FiRefreshCw, FiTruck } from "react-icons/fi";
import PivotableBiaxialBarChart from "../../components/chart/PivotableBiaxialBarChart";
import RadarChart from "../../components/chart/RadarChart";
import { MainContainer } from "../../components/container/MainContainer";
import SelectBox from "../../components/input/SelectBox";
import { Supplier_Reports } from "../../db_access/supplier_reports";
import { ReportStatus } from "../Client_Directory/ReportTypes";
import { TableHeaderContainer } from "../../components/container/TableHeaderContainer";
import { TableTitleContainer } from "../../components/container/TableTitleContainer";
import "../../style/pages-style/Engagement/engagement.css";
import { getOrgTokens, ORG_PALETTE } from "../../theme/style/organizationPalette";

const Supplier_Directory_Comparison = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);
  const [refresh, setRefresh] = useState(false);
  const [reports, setReports] = useState([]);

  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [status, setStatus] = useState("Accepted");
  const [emissionWeight, setEmissionWeight] = useState(50);
  const costWeight = 100 - emissionWeight;

  const [optionYears, setOptionYears] = useState([]);

  useEffect(() => {
    Supplier_Reports.get_mine_received().then((res) => res.json()).then(setReports);
  }, [refresh]);

  useEffect(() => {
    setOptionYears(reports.reduce((acc, r) => {
      if (!acc.some((c) => c.value === r.year)) acc.push({ label: String(r.year), value: r.year });
      return acc;
    }, []));
  }, [reports]);

  const processedData = useMemo(() => {
    const temp = new Map();
    let filtered = [...reports];
    if (selectedYear) filtered = filtered.filter(r => r.year === selectedYear);
    if (status) filtered = filtered.filter(r => r.status === status);

    filtered.forEach((report) => {
      const gwp = (report.gwp || 0) / 1000; // Convert to tonnes
      const cost = report.cost || 0;
      const score = (emissionWeight * gwp + costWeight * cost) / 100;

      if (temp.has(report.sender_id)) {
        const ex = temp.get(report.sender_id);
        ex.gwp += gwp; ex.cost += cost; ex.score += score; ex.reports.push(report);
      } else {
        temp.set(report.sender_id, {
          name: report.sender_name, id: report.sender_id,
          gwp, cost, score, reports: [report],
        });
      }
    });
    return Array.from(temp.values());
  }, [reports, selectedYear, status, emissionWeight, costWeight]);

  const selectedData = useMemo(() => processedData.filter((c) => selectedCompanies.includes(c.id)), [processedData, selectedCompanies]);

  return (
    <MainContainer>
      {/* ── Analysis Filters ────────────────────────────────── */}
      <Box sx={{ animation: "eng-fadeInUp 0.4s ease", mb: ORG_PALETTE.spacing.pageGap }}>
        <Paper elevation={0} sx={{ p: ORG_PALETTE.spacing.chartPad, borderRadius: ORG_PALETTE.radius.card, border: "1px solid", borderColor: tok.border.card, bgcolor: tok.bg.card, boxShadow: tok.shadow.card }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: ORG_PALETTE.spacing.sectionGap }}>
            <SelectBox label={"Target Year"} placeholder={"Filter by Year"} options={optionYears} value={selectedYear} onChange={(e, v) => setSelectedYear(v?.value || null)} />
            <SelectBox label={"Validation Status"} placeholder={"Filter by Status"}
              options={ReportStatus.map(s => ({ label: t(s), value: s }))}
              value={status} onChange={(e, v) => setStatus(v?.value || null)} />

            <Box>
              <Typography sx={{ textAlign: "left", fontSize: 13, fontWeight: 700, mb: 1.5, color: "text.secondary" }}>
                Benchmark Weighting (GWP vs Cost)
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#16a34a" }}>GWP: {emissionWeight}%</Typography>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#d97706" }}>Cost: {costWeight}%</Typography>
              </Box>
              <Slider value={emissionWeight} onChange={(_, v) => setEmissionWeight(v)} size="small" sx={{ color: "#16a34a" }} />
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* ── Selection & Analytics Workspace ──────────────────────── */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: ORG_PALETTE.spacing.pageGap, animation: "eng-fadeInUp 0.4s ease Stagger-2" }}>

        {/* Selection Library */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography sx={{ textAlign: "left", fontSize: ORG_PALETTE.font.size.sectionTitle, fontWeight: ORG_PALETTE.font.weight.semibold, color: tok.text.primary, textTransform: "uppercase", letterSpacing: 1.2 }}>
            {t("Supplier Registry")} ({processedData.length})
          </Typography>
          <Paper elevation={0} sx={{ p: ORG_PALETTE.spacing.chartPad, borderRadius: ORG_PALETTE.radius.card, border: "1px solid", borderColor: tok.border.card, bgcolor: tok.bg.card, boxShadow: tok.shadow.card }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 2 }}>
              {processedData.map((c) => (
                <Box key={c.id}
                  onClick={() => {
                    if (selectedCompanies.includes(c.id)) setSelectedCompanies(p => p.filter(id => id !== c.id));
                    else setSelectedCompanies(p => [...p, c.id]);
                  }}
                  sx={{
                    p: 2, borderRadius: ORG_PALETTE.radius.card, cursor: "pointer", border: "1.5px solid",
                    borderColor: selectedCompanies.includes(c.id) ? "#16a34a" : "divider",
                    background: selectedCompanies.includes(c.id) ? "rgba(22, 163, 74, 0.05)" : "transparent",
                    transition: "all 0.22s ease",
                    "&:hover": { borderColor: "#16a34a", background: "rgba(22, 163, 74, 0.02)" },
                    display: "flex", alignItems: "center", gap: 2
                  }}>
                  <Box sx={{
                    width: 24, height: 24, borderRadius: "6px", border: "2px solid",
                    borderColor: selectedCompanies.includes(c.id) ? "#16a34a" : "#cbd5e1",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: selectedCompanies.includes(c.id) ? "#16a34a" : "transparent"
                  }}>
                    {selectedCompanies.includes(c.id) && <FiCheck size={14} color="white" />}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{c.name}</Typography>
                    <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{c.reports.length} Records Visualized</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>
        </Box>

        {/* Charts Section */}
        <Box>
          {selectedData.length > 0 ? (
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(500px, 1fr))", gap: ORG_PALETTE.spacing.sectionGap }}>
              <Paper variant="Table Container" sx={{ borderRadius: ORG_PALETTE.radius.card, borderColor: tok.border.card, bgcolor: tok.bg.card, boxShadow: tok.shadow.card, overflow: "hidden" }}>
                <TableHeaderContainer>
                  <TableTitleContainer>
                    <Typography variant="Table Title">Supply Chain Biaxial Performance</Typography>
                  </TableTitleContainer>
                </TableHeaderContainer>
                <Box sx={{ p: 2, height: 450 }}>
                  <PivotableBiaxialBarChart
                    usages={selectedData}
                    title={""}
                    groupBy={"name"}
                    countBys={[
                      { name: "gwp", axis: "left", label: "GWP (tCO2-eq)" },
                      { name: "cost", axis: "right", label: "Financial Spend (₹)" },
                    ]}
                  />
                </Box>
              </Paper>
              <Paper variant="Table Container" sx={{ borderRadius: ORG_PALETTE.radius.card, borderColor: tok.border.card, bgcolor: tok.bg.card, boxShadow: tok.shadow.card, overflow: "hidden" }}>
                <TableHeaderContainer>
                  <TableTitleContainer>
                    <Typography variant="Table Title">Strategic Partner Intensity</Typography>
                  </TableTitleContainer>
                </TableHeaderContainer>
                <Box sx={{ p: 2, height: 450 }}>
                  <RadarChart
                    data={[
                      { field: "gwp", label: "GWP Index" },
                      { field: "cost", label: "Value Index" },
                      { field: "reports_count", label: "Data Quality" },
                      { field: "score", label: "Sustainability Score" },
                    ].map((f) => {
                      const row = { axis: f.label };
                      selectedData.forEach(c => {
                        row[c.name] = f.field === "reports_count" ? c.reports.length : c[f.field];
                      });
                      return row;
                    })}
                    items_name={selectedData.map(c => c.name)}
                    title=""
                  />
                </Box>
              </Paper>
            </Box>
          ) : (
            <Paper sx={{ p: 8, textAlign: "center", borderRadius: ORG_PALETTE.radius.card, border: "1px dashed", borderColor: "divider", background: "transparent" }}>
              <FiTruck size={48} color="#94a3b8" style={{ marginBottom: 16, opacity: 0.4 }} />
              <Typography sx={{ color: "text.secondary", fontWeight: 700, fontSize: 18 }}>Supplier Benchmark Portal</Typography>
              <Typography sx={{ color: "text.secondary", fontSize: 13, mt: 1 }}>Select partners from the library to generate comparative visibility</Typography>
            </Paper>
          )}
        </Box>
      </Box>
    </MainContainer>
  );
};

export default Supplier_Directory_Comparison;
